import React, { useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import {
    Paper,
    Box,
    CircularProgress,
    Divider,
    Typography,
} from '@mui/material';

import { useQueryReadFile } from 'src/modules/hooks/apis/no-sql/download';

// IMPORTANT FOR STYLING PDF VIEWER
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

import { ToolbarSlot } from '@react-pdf-viewer/toolbar';
import { SelectionMode } from '@react-pdf-viewer/selection-mode';
import { handleNoSqlApiError } from 'src/modules/hooks/apis/no-sql/helpers';
import Toast, { Severity } from '../controlled/Toast';
import packageJson from '../../../../../package.json';
import { pdfjs } from 'react-pdf';
import { MoreActionsPopover } from '@react-pdf-viewer/toolbar';

const VerticalToolbar = ({
    toolbarSlot,
    onSelectionModeChange,
}: {
    toolbarSlot: ToolbarSlot;
    onSelectionModeChange: (mode: SelectionMode) => void;
}) => {
    const handleSelectionModeChange = (mode: SelectionMode) => {
        onSelectionModeChange(mode);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                // Make it scrollable if needed
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {toolbarSlot.ShowSearchPopover({})}
            </Box>
            <Divider sx={{ width: '100%', m: '20%', p: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flex: 1,
                }}
            >
                <MoreActionsPopover toolbarSlot={toolbarSlot} />
            </Box>

            <Divider sx={{ width: '100%', m: '20%', p: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flex: 1,
                }}
            >
                {toolbarSlot.GoToPreviousPage({})}
                {toolbarSlot.CurrentPageLabel({})}
                {toolbarSlot.GoToNextPage({})}
            </Box>
            <Divider sx={{ width: '100%', m: '20%', p: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flex: 1,
                }}
            >
                {toolbarSlot.ZoomIn({})}
                {toolbarSlot.CurrentScale({})}
                {toolbarSlot.ZoomOut({})}
            </Box>
            <Divider sx={{ width: '100%', m: '20%', p: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flex: 1,
                }}
            >
                <div
                    style={{ marginBottom: '5%' }}
                    onClick={() =>
                        handleSelectionModeChange(SelectionMode.Text)
                    }
                >
                    {toolbarSlot.SwitchSelectionMode({
                        mode: SelectionMode.Text,
                    })}
                </div>
                <div
                    onClick={() =>
                        handleSelectionModeChange(SelectionMode.Hand)
                    }
                >
                    {toolbarSlot.SwitchSelectionMode({
                        mode: SelectionMode.Hand,
                    })}
                </div>
            </Box>
            <Divider sx={{ width: '100%', m: '20%', p: 0 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flex: 1,
                }}
            >
                {toolbarSlot.Print({})}
                {toolbarSlot.Download({})}
            </Box>
        </Box>
    );
};

interface PdfViewerProps {
    invoiceUrl: string;
    toolbarStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    useUploadFailureQuery?: boolean;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ invoiceUrl, toolbarStyle }) => {
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;

    const {
        data: documentData,
        error: documentError,
        isError: documentIsError,
        isLoading: documentIsLoading,
    } = useQueryReadFile(invoiceUrl);

    const [selectionMode, setSelectionMode] = useState(SelectionMode.Text);
    const [isToastOpen, setIsToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<Severity>('error');

    useEffect(() => {
        handleNoSqlApiError(documentError, setToastMessage, setToastSeverity);
        setIsToastOpen(documentIsError);
    }, [documentError, documentIsError]);

    if (documentIsLoading) {
        return <CircularProgress />;
    }

    const handleSelectionModeChange = (mode: SelectionMode) => {
        setSelectionMode(mode);
    };

    // Ensure pdfjs-dist version matches what's in package.json
    const pdfjsVersion = packageJson.dependencies['pdfjs-dist'].replace(
        '^',
        ''
    );
    if (pdfjsVersion !== pdfjs.version) {
        console.warn(
            `pdfjsVersion: ${pdfjsVersion} !== pdfjs.version: ${pdfjs.version}:: this may cause pdf viewer to fail.`
        );
    }

    return (
        <>
            <Toast
                message={toastMessage}
                severity={toastSeverity}
                isOpen={isToastOpen}
                onClose={() => setIsToastOpen(false)}
                id="toast-pdf-viewer"
            />

            <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}
            >
                <Paper
                    sx={{
                        // Row direction so the toolbar is on the left, PDF on the right
                        display: 'flex',
                        flexDirection: 'row',
                        height: '90vh',
                        minHeight: '600px',
                    }}
                >
                    {/* Left side: vertical toolbar */}
                    <Box
                        sx={{
                            // Provide a fixed or min width for the toolbar area
                            width: '60px',
                            backgroundColor: '#F5F5F5',
                            borderRight: '1px solid rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '8px',
                            ...toolbarStyle,
                        }}
                    >
                        <Toolbar>
                            {toolbarSlot => (
                                <VerticalToolbar
                                    toolbarSlot={toolbarSlot}
                                    onSelectionModeChange={
                                        handleSelectionModeChange
                                    }
                                />
                            )}
                        </Toolbar>
                    </Box>

                    {/* Right side: PDF viewer */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            // Removing "justifyContent" and "alignItems"
                            // so the PDF can expand and scroll
                        }}
                    >
                        {documentIsError || !documentData ? (
                            <Typography variant="body1" color="textSecondary">
                                No PDF available or failed to load.
                            </Typography>
                        ) : (
                            <Viewer
                                fileUrl={documentData}
                                plugins={[toolbarPluginInstance]}
                                defaultScale={1.0}
                            />
                        )}
                    </Box>
                </Paper>
            </Worker>
        </>
    );
};

const PdfViewerInvoiceInfoModal: React.FC<PdfViewerProps> = ({
    invoiceUrl,
    useUploadFailureQuery = false,
}) => {
    const toolbarPluginInstance = toolbarPlugin();

    const { Toolbar } = toolbarPluginInstance;

    const {
        data: documentData,
        error: documentError,
        isError: documentIsError,
        isLoading: documentIsLoading,
    } = useQueryReadFile(invoiceUrl, useUploadFailureQuery);

    const [isToastOpen, setIsToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<Severity>('error');

    useEffect(() => {
        handleNoSqlApiError(documentError, setToastMessage, setToastSeverity);
        setIsToastOpen(documentIsError);
    }, [documentError, documentIsError]);

    if (documentIsLoading) {
        return <CircularProgress />;
    }

    const pdfjsVersion = packageJson.dependencies['pdfjs-dist'].replace(
        '^',
        ''
    );
    if (pdfjsVersion !== pdfjs.version) {
        console.warn(
            `pdfjsVersion: ${pdfjsVersion} !== pdfjs.version: ${pdfjs.version}:: this may cause pdf viewer to fail.`
        );
    }

    return (
        <>
            <Toast
                message={toastMessage}
                severity={toastSeverity}
                isOpen={isToastOpen}
                onClose={() => setIsToastOpen(false)}
                id="toast-pdf-viewer"
            />
            <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}
            >
                <Paper
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '90vh',
                        minHeight: '600px',
                    }}
                >
                    {documentIsLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : documentIsError || !documentData ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="body1" color="textSecondary">
                                No PDF available or failed to load.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Top toolbar container */}
                            <Box
                                sx={{
                                    backgroundColor: '#eeeeee',
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.1)',
                                    display: 'flex',
                                    padding: '4px',
                                }}
                            >
                                <Toolbar />
                            </Box>

                            {/* PDF Viewer container */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Viewer
                                    fileUrl={documentData}
                                    plugins={[toolbarPluginInstance]}
                                    defaultScale={1.0}
                                />
                            </Box>
                        </>
                    )}
                </Paper>
            </Worker>
        </>
    );
};

export default PdfViewer;
export { PdfViewerInvoiceInfoModal };
